import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Users, User, CheckCircle, XCircle, Star, Clock, Loader2, Eye, Mail, Bell, Download, Trash2, Pencil, FileDown, X, Check, Package, Phone, MapPin, Truck } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const styles = {
    pending: { bg: "rgba(247,208,70,0.15)", color: "var(--ll-accent-hover)", label: "Pending" },
    approved: { bg: "rgba(163,184,153,0.2)", color: "var(--ll-green)", label: "Approved" },
    rejected: { bg: "rgba(232,124,124,0.1)", color: "#e87c7c", label: "Rejected" },
  };
  const s = styles[status] || styles.pending;
  return <span className="text-[10px] font-medium px-2.5 py-1 rounded-full" style={{ background: s.bg, color: s.color, fontFamily: "'Outfit', sans-serif" }}>{s.label}</span>;
};

const font = { fontFamily: "'Outfit', sans-serif" };

const ConfirmDelete = ({ onConfirm, onCancel }) => (
  <div className="flex items-center gap-1.5">
    <span className="text-[10px] mr-1" style={{ color: "#e87c7c", ...font }}>Delete?</span>
    <button onClick={onConfirm} className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: "#e87c7c", color: "white" }} data-testid="confirm-delete-btn"><Check size={12} /></button>
    <button onClick={onCancel} className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: "var(--ll-bg-secondary)", color: "var(--ll-text-muted)" }}><X size={12} /></button>
  </div>
);

const InlineEdit = ({ item, onSave, onCancel }) => {
  const [name, setName] = useState(item.name || "");
  const [email, setEmail] = useState(item.email || "");
  return (
    <div className="flex items-center gap-2 flex-1">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" data-testid="edit-name-input"
        className="text-sm px-2 py-1 rounded-lg border w-28" style={{ borderColor: "var(--ll-border)", ...font }} />
      <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="edit-email-input"
        className="text-sm px-2 py-1 rounded-lg border flex-1" style={{ borderColor: "var(--ll-border)", ...font }} />
      <button onClick={() => onSave(name, email)} className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: "var(--ll-green)", color: "white" }}><Check size={12} /></button>
      <button onClick={onCancel} className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: "var(--ll-bg-secondary)", color: "var(--ll-text-muted)" }}><X size={12} /></button>
    </div>
  );
};

const ListHeader = ({ icon: Icon, iconStyle, label, count, countStyle, onExport, exporting }) => (
  <div className="px-5 py-3 flex items-center justify-between" style={iconStyle}>
    <div className="flex items-center gap-2">
      <Icon size={14} style={{ color: iconStyle.iconColor }} />
      <span className="text-sm font-medium" style={{ color: "var(--ll-text)", ...font }}>{label}</span>
    </div>
    <div className="flex items-center gap-2">
      <span className="text-xs px-2 py-0.5 rounded-full" style={countStyle}>{count}</span>
      <button onClick={onExport} disabled={exporting} data-testid={`export-${label.toLowerCase().replace(/\s/g, "-")}`}
        className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:opacity-70"
        style={{ background: "var(--ll-bg-secondary)", color: "var(--ll-text-muted)" }}
        title="Export CSV"><FileDown size={12} /></button>
    </div>
  </div>
);

const SUBSCRIBER_ICON_STYLE = { background: "var(--ll-bg-secondary)", iconColor: "var(--ll-green)" };
const SUBSCRIBER_COUNT_STYLE = { background: "var(--ll-green)", color: "white", ...font };
const WAITLIST_ICON_STYLE = { background: "rgba(247,208,70,0.1)", iconColor: "var(--ll-accent-hover)" };
const WAITLIST_COUNT_STYLE = { background: "var(--ll-accent)", color: "var(--ll-text)", ...font };

const RowActions = ({ id, collection, deleting, onDelete, onConfirmDelete, onCancelDelete, onEdit }) => (
  <div className="flex items-center gap-1 flex-shrink-0">
    {deleting === id ? (
      <ConfirmDelete onConfirm={() => onConfirmDelete(collection, id)} onCancel={onCancelDelete} />
    ) : (
      <>
        {(collection === "subscribers" || collection === "waitlist") && (
          <button onClick={() => onEdit(id)} className="w-6 h-6 rounded-full flex items-center justify-center transition-all hover:opacity-70"
            style={{ background: "var(--ll-bg-secondary)", color: "var(--ll-text-muted)" }} title="Edit"><Pencil size={11} /></button>
        )}
        <button onClick={() => onDelete(id)} data-testid={`delete-${id}`}
          className="w-6 h-6 rounded-full flex items-center justify-center transition-all hover:opacity-70"
          style={{ background: "rgba(232,124,124,0.1)", color: "#e87c7c" }} title="Delete"><Trash2 size={11} /></button>
      </>
    )}
  </div>
);

const AdminPage = () => {
  const [password, setPassword] = useState("");
  const [loggedIn, setLoggedIn] = useState(false);
  const [tab, setTab] = useState("subscribers");
  const [applications, setApplications] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [physicalOrders, setPhysicalOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [editing, setEditing] = useState(null);
  const [exporting, setExporting] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { password });
      setLoggedIn(true);
      toast.success("Logged in.");
      fetchData();
    } catch { toast.error("Invalid password."); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appRes, revRes, subRes, waitRes, physRes] = await Promise.all([
        axios.post(`${API}/admin/applications`, { password }),
        axios.post(`${API}/admin/reviews`, { password }),
        axios.post(`${API}/admin/subscribers`, { password }),
        axios.post(`${API}/admin/waitlist`, { password }),
        axios.post(`${API}/admin/physical-orders`, { password }),
      ]);
      setApplications(appRes.data.applications || []);
      setReviews(revRes.data.reviews || []);
      setSubscribers(subRes.data.subscribers || []);
      setWaitlist(waitRes.data.waitlist || []);
      setPhysicalOrders(physRes.data.orders || []);
    } catch { toast.error("Failed to load admin data."); }
    finally { setLoading(false); }
  };

  const handleDelete = async (collection, itemId) => {
    try {
      await axios.post(`${API}/admin/delete`, { password, collection, item_id: itemId });
      toast.success("Deleted.");
      setDeleting(null);
      fetchData();
    } catch { toast.error("Failed to delete."); }
  };

  const handleEdit = async (collection, itemId, name, email) => {
    try {
      await axios.post(`${API}/admin/edit`, { password, collection, item_id: itemId, name, email });
      toast.success("Updated.");
      setEditing(null);
      fetchData();
    } catch { toast.error("Failed to update."); }
  };

  const handleFulfill = async (orderId) => {
    try {
      await axios.post(`${API}/admin/physical-orders/fulfill`, { password, application_id: orderId });
      toast.success("Order marked as fulfilled.");
      fetchData();
    } catch { toast.error("Failed to update order."); }
  };


  const handleExport = async (listType) => {
    setExporting(true);
    try {
      const res = await axios.post(`${API}/admin/export/${listType}`, { password });
      if (!res.data.csv) { toast.info("No data to export."); return; }
      const blob = new Blob([res.data.csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.data.filename || `${listType}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${res.data.count} records.`);
    } catch { toast.error("Export failed."); }
    finally { setExporting(false); }
  };

  const approveApp = async (appId) => {
    try {
      const res = await axios.post(`${API}/admin/approve`, { password, application_id: appId, days_valid: 30 });
      toast.success(`Pass sent: ${res.data.pass_code} to ${res.data.email}`);
      fetchData();
    } catch { toast.error("Failed to approve."); }
  };

  const rejectApp = async (appId) => {
    try {
      await axios.post(`${API}/admin/reject`, { password, application_id: appId });
      toast.success("Application rejected.");
      fetchData();
    } catch { toast.error("Failed to reject."); }
  };

  const approveReview = async (reviewId) => {
    try {
      await axios.post(`${API}/admin/review/approve`, { password, application_id: reviewId });
      toast.success("Review approved and published.");
      fetchData();
    } catch { toast.error("Failed to approve review."); }
  };

  const subscribersByProduct = subscribers.reduce((acc, sub) => {
    const key = sub.product_name || sub.product_id || "Unknown";
    if (!acc[key]) acc[key] = [];
    acc[key].push(sub);
    return acc;
  }, {});

  const waitlistByProduct = waitlist.reduce((acc, entry) => {
    const key = entry.product_name || entry.product_id || "Unknown";
    if (!acc[key]) acc[key] = [];
    acc[key].push(entry);
    return acc;
  }, {});

  if (!loggedIn) {
    return (
      <main data-testid="admin-page" className="pt-20">
        <section className="py-24 md:py-32 min-h-[70vh] flex items-center justify-center">
          <form onSubmit={login} className="w-full max-w-sm mx-auto px-6 space-y-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: "var(--ll-bg-secondary)" }}>
              <Lock size={20} style={{ color: "var(--ll-text-muted)" }} />
            </div>
            <h1 className="font-serif text-2xl font-medium text-center mb-4" style={{ color: "var(--ll-text)" }}>Admin Panel</h1>
            <div className="space-y-2">
              <Label className="text-sm" style={{ color: "var(--ll-text-secondary)", ...font }}>Password</Label>
              <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Enter admin password" className="rounded-xl border" style={{ borderColor: "var(--ll-border)" }} />
            </div>
            <button type="submit" data-testid="admin-login-btn" className="w-full rounded-full px-6 py-3 text-sm font-medium" style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", ...font }}>Login</button>
          </form>
        </section>
      </main>
    );
  }

  const TABS = [
    { key: "physical-orders", label: "Orders", icon: Package, count: physicalOrders.filter((o) => o.fulfillment_status === "pending" && o.payment_status === "COMPLETE").length },
    { key: "subscribers", label: "Subscribers", icon: Mail, count: subscribers.length },
    { key: "waitlist", label: "Waitlist", icon: Bell, count: waitlist.length },
    { key: "applications", label: "Beta Apps", icon: Users, count: applications.filter((a) => a.status === "pending").length },
    { key: "reviews", label: "Reviews", icon: Star, count: reviews.filter((r) => !r.approved).length },
  ];

  const rowProps = { deleting, onDelete: setDeleting, onConfirmDelete: handleDelete, onCancelDelete: () => setDeleting(null), onEdit: setEditing };

  return (
    <main data-testid="admin-page" className="pt-20">
      <section className="py-12 md:py-16 min-h-screen">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h1 className="font-serif text-3xl font-medium mb-8" style={{ color: "var(--ll-text)" }}>Admin Panel</h1>

          <div className="flex gap-1 mb-8 p-1 rounded-xl overflow-x-auto" style={{ background: "var(--ll-bg-secondary)" }}>
            {TABS.map(({ key, label, icon: TabIcon, count }) => (
              <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap"
                style={{ background: tab === key ? "white" : "transparent", color: tab === key ? "var(--ll-text)" : "var(--ll-text-muted)", boxShadow: tab === key ? "0 1px 4px rgba(0,0,0,0.06)" : "none", ...font }}>
                <TabIcon size={14} /> {label}
                {count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: "var(--ll-accent)", color: "var(--ll-text)" }}>{count}</span>}
              </button>
            ))}
          </div>

          {loading && <div className="text-center py-12"><Loader2 size={24} className="animate-spin mx-auto" style={{ color: "var(--ll-text-muted)" }} /></div>}


          {/* Physical Orders Tab */}
          {!loading && tab === "physical-orders" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm" style={{ color: "var(--ll-text-muted)", ...font }}>
                  {physicalOrders.length} orders · {physicalOrders.filter((o) => o.fulfillment_status === "pending" && o.payment_status === "COMPLETE").length} awaiting fulfillment
                </p>
                <button onClick={() => handleExport("physical-orders")} disabled={exporting} data-testid="export-all-orders"
                  className="rounded-full px-4 py-2 text-xs font-medium flex items-center gap-1.5 transition-all hover:-translate-y-0.5"
                  style={{ background: "var(--ll-bg-secondary)", color: "var(--ll-text)", ...font }}>
                  <FileDown size={12} /> Export CSV
                </button>
              </div>
              {physicalOrders.length === 0 && <p className="text-sm py-8 text-center" style={{ color: "var(--ll-text-muted)", ...font }}>No physical orders yet.</p>}
              {physicalOrders.map((order) => {
                const d = order.delivery || {};
                const isPaid = order.payment_status === "COMPLETE";
                const isFulfilled = order.fulfillment_status === "fulfilled";
                return (
                  <div key={order.id || order.m_payment_id} data-testid={`order-${order.m_payment_id}`}
                    className="p-5 rounded-2xl" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-medium" style={{ color: "var(--ll-text)", ...font }}>{order.product_name}</p>
                          <span className="text-[10px] font-medium px-2.5 py-1 rounded-full"
                            style={{ background: isPaid ? "rgba(163,184,153,0.2)" : "rgba(247,208,70,0.15)", color: isPaid ? "var(--ll-green)" : "var(--ll-accent-hover)", ...font }}>
                            {isPaid ? "Paid" : order.payment_status}
                          </span>
                          <span className="text-[10px] font-medium px-2.5 py-1 rounded-full"
                            style={{ background: isFulfilled ? "rgba(163,184,153,0.2)" : "rgba(232,124,124,0.1)", color: isFulfilled ? "var(--ll-green)" : "#e87c7c", ...font }}>
                            {isFulfilled ? "Shipped" : "Awaiting Shipment"}
                          </span>
                        </div>
                        <p className="text-xs" style={{ color: "var(--ll-text-muted)", ...font }}>
                          Ref: {order.m_payment_id} · R{order.amount} · {new Date(order.created_at).toLocaleDateString("en-ZA")}
                        </p>
                      </div>
                    </div>

                    <div className="rounded-xl p-4 space-y-2 mb-3" style={{ background: "var(--ll-bg-secondary)" }}>
                      <p className="text-[10px] tracking-[0.15em] uppercase mb-1" style={{ color: "var(--ll-text-muted)", ...font }}>Delivery Details — Copy for printing company</p>
                      <div className="flex items-start gap-2">
                        <User size={13} style={{ color: "var(--ll-text-muted)", marginTop: 2 }} />
                        <p className="text-sm font-medium" style={{ color: "var(--ll-text)", ...font }}>{d.recipient_name || order.customer_name}</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <MapPin size={13} style={{ color: "var(--ll-text-muted)", marginTop: 2 }} />
                        <p className="text-xs" style={{ color: "var(--ll-text-secondary)", ...font }}>
                          {d.pudo_location || [d.delivery_address, d.city, d.province, d.postal_code].filter(Boolean).join(", ")}
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <Phone size={13} style={{ color: "var(--ll-text-muted)", marginTop: 2 }} />
                        <p className="text-xs" style={{ color: "var(--ll-text-secondary)", ...font }}>{d.recipient_mobile}</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <Mail size={13} style={{ color: "var(--ll-text-muted)", marginTop: 2 }} />
                        <p className="text-xs" style={{ color: "var(--ll-text-secondary)", ...font }}>{order.customer_email}</p>
                      </div>
                      {d.delivery_notes && (
                        <p className="text-xs italic mt-1" style={{ color: "var(--ll-text-muted)", ...font }}>Note: {d.delivery_notes}</p>
                      )}
                    </div>

                    {isPaid && !isFulfilled && (
                      <button onClick={() => handleFulfill(order.id)} data-testid={`fulfill-${order.m_payment_id}`}
                        className="rounded-full px-5 py-2 text-xs font-medium flex items-center gap-1.5 transition-all hover:-translate-y-0.5"
                        style={{ background: "var(--ll-green)", color: "white", ...font }}>
                        <Truck size={12} /> Mark as Shipped
                      </button>
                    )}
                    {isFulfilled && order.fulfilled_at && (
                      <p className="text-xs" style={{ color: "var(--ll-green)", ...font }}>Shipped on {new Date(order.fulfilled_at).toLocaleDateString("en-ZA")}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}


          {/* Subscribers Tab */}
          {!loading && tab === "subscribers" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <p className="text-sm" style={{ color: "var(--ll-text-muted)", ...font }}>{subscribers.length} total subscribers across {Object.keys(subscribersByProduct).length} products</p>
                <button onClick={() => handleExport("subscribers")} disabled={exporting} data-testid="export-all-subscribers"
                  className="rounded-full px-4 py-2 text-xs font-medium flex items-center gap-1.5 transition-all hover:-translate-y-0.5"
                  style={{ background: "var(--ll-bg-secondary)", color: "var(--ll-text)", ...font }}>
                  <FileDown size={12} /> Export All CSV
                </button>
              </div>
              {Object.keys(subscribersByProduct).length === 0 && <p className="text-sm py-8 text-center" style={{ color: "var(--ll-text-muted)", ...font }}>No subscribers yet.</p>}
              {Object.entries(subscribersByProduct).map(([productName, subs]) => (
                <div key={productName} className="rounded-2xl overflow-hidden" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
                  <ListHeader icon={Download} iconStyle={SUBSCRIBER_ICON_STYLE} label={productName} count={subs.length}
                    countStyle={SUBSCRIBER_COUNT_STYLE} onExport={() => handleExport("subscribers")} exporting={exporting} />
                  <div className="divide-y" style={{ borderColor: "var(--ll-border)" }}>
                    {subs.map((sub) => (
                      <div key={sub.id || sub.email} className="px-5 py-3 flex items-center justify-between gap-3">
                        {editing === (sub.id || sub.email) ? (
                          <InlineEdit item={sub} onSave={(n, e) => handleEdit("subscribers", sub.id, n, e)} onCancel={() => setEditing(null)} />
                        ) : (
                          <>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm truncate" style={{ color: "var(--ll-text)", ...font }}>{sub.name || "\u2014"} <span className="text-xs" style={{ color: "var(--ll-text-muted)" }}>({sub.email})</span></p>
                            </div>
                            <p className="text-[10px] flex-shrink-0" style={{ color: "var(--ll-text-muted)", ...font }}>{sub.created_at ? new Date(sub.created_at).toLocaleDateString("en-ZA") : "\u2014"}</p>
                            <RowActions id={sub.id || sub.email} collection="subscribers" {...rowProps} />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Waitlist Tab */}
          {!loading && tab === "waitlist" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <p className="text-sm" style={{ color: "var(--ll-text-muted)", ...font }}>{waitlist.length} people on waitlists</p>
                <button onClick={() => handleExport("waitlist")} disabled={exporting} data-testid="export-all-waitlist"
                  className="rounded-full px-4 py-2 text-xs font-medium flex items-center gap-1.5 transition-all hover:-translate-y-0.5"
                  style={{ background: "var(--ll-bg-secondary)", color: "var(--ll-text)", ...font }}>
                  <FileDown size={12} /> Export All CSV
                </button>
              </div>
              {Object.keys(waitlistByProduct).length === 0 && <p className="text-sm py-8 text-center" style={{ color: "var(--ll-text-muted)", ...font }}>No waitlist entries yet.</p>}
              {Object.entries(waitlistByProduct).map(([productName, entries]) => (
                <div key={productName} className="rounded-2xl overflow-hidden" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
                  <ListHeader icon={Bell} iconStyle={WAITLIST_ICON_STYLE} label={productName} count={entries.length}
                    countStyle={WAITLIST_COUNT_STYLE} onExport={() => handleExport("waitlist")} exporting={exporting} />
                  <div className="divide-y" style={{ borderColor: "var(--ll-border)" }}>
                    {entries.map((entry) => (
                      <div key={entry.id || entry.email} className="px-5 py-3 flex items-center justify-between gap-3">
                        {editing === (entry.id || entry.email) ? (
                          <InlineEdit item={entry} onSave={(n, e) => handleEdit("waitlist", entry.id, n, e)} onCancel={() => setEditing(null)} />
                        ) : (
                          <>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm truncate" style={{ color: "var(--ll-text)", ...font }}>{entry.name || "\u2014"} <span className="text-xs" style={{ color: "var(--ll-text-muted)" }}>({entry.email})</span></p>
                            </div>
                            <p className="text-[10px] flex-shrink-0" style={{ color: "var(--ll-text-muted)", ...font }}>{entry.created_at ? new Date(entry.created_at).toLocaleDateString("en-ZA") : "\u2014"}</p>
                            <RowActions id={entry.id || entry.email} collection="waitlist" {...rowProps} />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Applications Tab */}
          {!loading && tab === "applications" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm" style={{ color: "var(--ll-text-muted)", ...font }}>{applications.length} applications</p>
                <button onClick={() => handleExport("applications")} disabled={exporting} data-testid="export-all-applications"
                  className="rounded-full px-4 py-2 text-xs font-medium flex items-center gap-1.5 transition-all hover:-translate-y-0.5"
                  style={{ background: "var(--ll-bg-secondary)", color: "var(--ll-text)", ...font }}>
                  <FileDown size={12} /> Export CSV
                </button>
              </div>
              {applications.length === 0 && <p className="text-sm py-8 text-center" style={{ color: "var(--ll-text-muted)", ...font }}>No applications yet.</p>}
              {applications.map((app) => (
                <div key={app.id} data-testid={`app-${app.id}`} className="p-5 rounded-2xl" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium" style={{ color: "var(--ll-text)", ...font }}>{app.name}</p>
                        <StatusBadge status={app.status} />
                      </div>
                      <p className="text-xs" style={{ color: "var(--ll-text-muted)", ...font }}>{app.email}</p>
                      {app.social_handle && <p className="text-xs mt-0.5" style={{ color: "var(--ll-text-muted)", ...font }}>{app.social_handle}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-[10px] flex items-center gap-1 flex-shrink-0" style={{ color: "var(--ll-text-muted)", ...font }}>
                        <Clock size={10} /> {new Date(app.created_at).toLocaleDateString("en-ZA")}
                      </p>
                      <RowActions id={app.id} collection="applications" {...rowProps} />
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed mb-4 p-3 rounded-xl" style={{ background: "var(--ll-bg-secondary)", color: "var(--ll-text-secondary)", ...font }}>
                    &ldquo;{app.reason}&rdquo;
                  </p>
                  {app.status === "pending" && (
                    <div className="flex gap-2">
                      <button data-testid={`approve-${app.id}`} onClick={() => approveApp(app.id)}
                        className="rounded-full px-4 py-2 text-xs font-medium flex items-center gap-1"
                        style={{ background: "var(--ll-green)", color: "white", ...font }}><CheckCircle size={12} /> Approve & Send Pass</button>
                      <button data-testid={`reject-${app.id}`} onClick={() => rejectApp(app.id)}
                        className="rounded-full px-4 py-2 text-xs font-medium flex items-center gap-1 border"
                        style={{ borderColor: "var(--ll-border)", color: "var(--ll-text-muted)", ...font }}><XCircle size={12} /> Reject</button>
                    </div>
                  )}
                  {app.status === "approved" && app.pass_code && <p className="text-xs" style={{ color: "var(--ll-green)", ...font }}>Pass: {app.pass_code}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Reviews Tab */}
          {!loading && tab === "reviews" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm" style={{ color: "var(--ll-text-muted)", ...font }}>{reviews.length} reviews</p>
                <button onClick={() => handleExport("reviews")} disabled={exporting} data-testid="export-all-reviews"
                  className="rounded-full px-4 py-2 text-xs font-medium flex items-center gap-1.5 transition-all hover:-translate-y-0.5"
                  style={{ background: "var(--ll-bg-secondary)", color: "var(--ll-text)", ...font }}>
                  <FileDown size={12} /> Export CSV
                </button>
              </div>
              {reviews.length === 0 && <p className="text-sm py-8 text-center" style={{ color: "var(--ll-text-muted)", ...font }}>No reviews yet.</p>}
              {reviews.map((rev) => (
                <div key={rev.id} data-testid={`review-${rev.id}`} className="p-5 rounded-2xl" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <p className="text-sm font-medium" style={{ color: "var(--ll-text)", ...font }}>{rev.name}</p>
                      <p className="text-xs" style={{ color: "var(--ll-text-muted)", ...font }}>{rev.email} · {rev.source === "beta" ? "Beta Tester" : "Purchaser"}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        {Array.from({ length: rev.rating }).map((_, i) => <Star key={`rev-star-${rev.id}-${i}`} size={12} fill="var(--ll-accent)" style={{ color: "var(--ll-accent)" }} />)}
                      </div>
                      <RowActions id={rev.id} collection="reviews" {...rowProps} />
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed mb-3 p-3 rounded-xl" style={{ background: "var(--ll-bg-secondary)", color: "var(--ll-text-secondary)", ...font }}>
                    &ldquo;{rev.review_text}&rdquo;
                  </p>
                  <div className="flex items-center gap-2">
                    {rev.approved
                      ? <span className="text-xs flex items-center gap-1" style={{ color: "var(--ll-green)", ...font }}><Eye size={12} /> Published</span>
                      : <button data-testid={`approve-review-${rev.id}`} onClick={() => approveReview(rev.id)} className="rounded-full px-4 py-2 text-xs font-medium flex items-center gap-1" style={{ background: "var(--ll-green)", color: "white", ...font }}><CheckCircle size={12} /> Approve & Publish</button>
                    }
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default AdminPage;
